import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Inventory2Outlined, PlaylistAddCheckOutlined, Refresh as RefreshIcon } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";

import CustomSnackBar from "../../Components/CustomSnackBar";
import MainText from "../../Components/MainText";
import ActiveGeneralInventoryAlert from "./components/ActiveGeneralInventoryAlert";
import CountGroupSetup, { serializeCountGroupRoles } from "./components/CountGroupSetup";
import StockCountPage from "./StockCountPage";
import { hasAnyGeneralInventoryPermission, hasPermission } from "../../config/permissionUtils";
import { useAuth } from "../../context/AuthContext";
import { inventoryService } from "../../services";
import useStockCount from "../../hooks/useStockCount";

const ACTIVE_STATUSES = ["DRAFT", "PENDING_REVIEW", "REVIEWING"];

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

const getStatusMeta = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "DRAFT") return { label: "En captura", color: "warning" };
  if (normalized === "PENDING_REVIEW") return { label: "Pendiente de revision", color: "info" };
  if (normalized === "REVIEWING") return { label: "En revision", color: "info" };
  if (normalized === "APPLIED") return { label: "Aplicado", color: "success" };
  if (normalized === "CANCELED") return { label: "Anulado", color: "default" };
  if (normalized === "CLOSED") return { label: "Cerrado", color: "info" };
  return { label: normalized || "-", color: "default" };
};

const formatBlockerValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  if (value instanceof Date) return value.toLocaleString();
  if (typeof value === "string") {
    const date = new Date(value);
    if (/^\d{4}-\d{2}-\d{2}T/.test(value) && !Number.isNaN(date.getTime())) {
      return date.toLocaleString();
    }
    return value;
  }
  return String(value);
};

const formatBlockerKey = (key) =>
  String(key || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

function BlockerDetails({ blocker }) {
  const details = toArray(blocker?.details);
  if (details.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No hay detalle adicional para este bloqueo.
      </Typography>
    );
  }
  return (
    <Stack spacing={1}>
      {details.map((detail, index) => {
        const productRows = toArray(detail?.products || detail?.items);
        const entries = Object.entries(detail || {}).filter(
          ([key, value]) =>
            !["products", "items", "resolve_url"].includes(key) &&
            value !== null &&
            value !== undefined &&
            value !== ""
        );
        return (
          <Paper key={`${detail?.id || "detail"}-${index}`} variant="outlined" sx={{ p: 1 }}>
            <Stack spacing={0.7}>
              <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  #{index + 1}
                </Typography>
                {detail?.type ? <Chip size="small" variant="outlined" label={detail.type} /> : null}
                {detail?.status ? <Chip size="small" label={detail.status} /> : null}
              </Stack>
              <Stack spacing={0.3}>
                {entries.map(([key, value]) => (
                  <Typography key={key} variant="caption" color="text.secondary" display="block">
                    <Box component="span" sx={{ fontWeight: 800, color: "text.primary" }}>
                      {formatBlockerKey(key)}:
                    </Box>{" "}
                    {formatBlockerValue(value)}
                  </Typography>
                ))}
              </Stack>
              {productRows.length > 0 ? (
                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                  {productRows.map((product, productIndex) => (
                    <Chip
                      key={`${detail?.id || index}-product-${productIndex}`}
                      size="small"
                      variant="outlined"
                      label={`${product?.product_code || "Sin codigo"} - ${product?.product_name || "Producto"}${
                        product?.qty_base ? ` (${product.qty_base} base)` : ""
                      }`}
                    />
                  ))}
                </Stack>
              ) : null}
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
}

function StockCountGeneralPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentBranch, isSuperuser, effectivePerms = [] } = useAuth();
  const branchId = currentBranch?.id || null;
  const canViewGeneral = hasPermission(effectivePerms, "inventory_general_view", isSuperuser);
  const canStartGeneral = hasPermission(effectivePerms, "inventory_general_start", isSuperuser);
  const canCountGeneral = hasPermission(effectivePerms, "inventory_general_count", isSuperuser);
  const canAdjustGeneral = hasPermission(effectivePerms, "inventory_general_adjust", isSuperuser);
  const canCancelGeneral = hasPermission(effectivePerms, "inventory_general_cancel", isSuperuser);
  const canAccessGeneral = hasAnyGeneralInventoryPermission(effectivePerms, isSuperuser);
  const canAccessCapture = canViewGeneral || canCountGeneral || canAdjustGeneral;
  const isNewRoute = location.pathname.endsWith("/new");
  const hasCountIdParam = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = Number(params.get("count_id"));
    return Number.isInteger(raw) && raw > 0;
  }, [location.search]);

  const stockCount = useStockCount("GENERAL", branchId);
  const {
    countId,
    countStatus,
    countAppliedAt,
    error: stockCountError,
    filterMode,
    filteredItems,
    isReadOnly,
    items,
    groups,
    crosses,
    loading: stockCountLoading,
    searchQuery,
    setFilterMode,
    setSearchQuery,
    addEntry,
    updateEntry,
    voidEntry,
    resetItem,
    submitCount,
    settingsSnapshot,
    refreshCount,
  } = stockCount;

  const [counts, setCounts] = useState([]);
  const [countsLoading, setCountsLoading] = useState(false);
  const [countSettings, setCountSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [countListFilter, setCountListFilter] = useState("ALL");
  const [countListSearch, setCountListSearch] = useState("");
  const [creatingCount, setCreatingCount] = useState(false);
  const [startDialogOpen, setStartDialogOpen] = useState(false);
  const [groupCountDraft, setGroupCountDraft] = useState(1);
  const [groupRolesDraft, setGroupRolesDraft] = useState([]);
  const [cancelDialog, setCancelDialog] = useState({ open: false, count: null });
  const [selectedBlocker, setSelectedBlocker] = useState(null);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");

  const activeGroupStorageKey = useMemo(
    () => (branchId && countId ? `stock-count-general-active-group:${branchId}:${countId}` : null),
    [branchId, countId]
  );

  const showSnack = useCallback((message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  }, []);

  const loadCounts = useCallback(async () => {
    if (!branchId || !canAccessGeneral) {
      setCounts([]);
      return;
    }

    setCountsLoading(true);
    try {
      const data = await inventoryService.listStockCounts({ branch_id: branchId, count_type: "GENERAL" });
      setCounts(toArray(data));
    } catch (error) {
      setCounts([]);
      showSnack(error?.response?.data?.detail || "No se pudo cargar el listado de inventarios.", "error");
    } finally {
      setCountsLoading(false);
    }
  }, [branchId, canAccessGeneral, showSnack]);

  const loadCountSettings = useCallback(async () => {
    if (!branchId || !canAccessGeneral) {
      setCountSettings(null);
      return;
    }
    setSettingsLoading(true);
    try {
      const data = await inventoryService.getCountSettings({ branch_id: branchId });
      setCountSettings(data || null);
    } catch {
      setCountSettings(null);
    } finally {
      setSettingsLoading(false);
    }
  }, [branchId, canAccessGeneral]);

  const handleRefresh = useCallback(async () => {
    await Promise.all([
      loadCounts(),
      loadCountSettings(),
      countId ? refreshCount().catch(() => null) : Promise.resolve(null),
    ]);
  }, [countId, loadCountSettings, loadCounts, refreshCount]);

  useEffect(() => {
    loadCounts();
    loadCountSettings();
  }, [loadCountSettings, loadCounts]);

  useEffect(() => {
    if (!activeGroupStorageKey) {
      setActiveGroupId(null);
      return;
    }
    const storedValue = window.sessionStorage.getItem(activeGroupStorageKey);
    setActiveGroupId(storedValue || null);
  }, [activeGroupStorageKey]);

  const updateActiveGroupId = useCallback(
    (nextGroupId) => {
      const normalized = nextGroupId ? String(nextGroupId) : "";
      setActiveGroupId(normalized || null);
      if (!activeGroupStorageKey) return;
      if (normalized) {
        window.sessionStorage.setItem(activeGroupStorageKey, normalized);
      } else {
        window.sessionStorage.removeItem(activeGroupStorageKey);
      }
    },
    [activeGroupStorageKey]
  );

  useEffect(() => {
    if (!countId || stockCountLoading || stockCountError || items.length > 0) {
      return;
    }
    refreshCount().catch(() => {});
  }, [countId, stockCountError, items.length, stockCountLoading, refreshCount]);

  const createCountGroup = useCallback(
    async (payload) => {
      if (!branchId || !countId) return null;
      const group = await inventoryService.createStockCountGroup(countId, {
        branch_id: branchId,
        ...payload,
      });
      await refreshCount();
      return group;
    },
    [branchId, countId, refreshCount]
  );

  const joinCountGroup = useCallback(
    async (groupId, payload = {}) => {
      if (!branchId || !countId || !groupId) return null;
      const group = await inventoryService.joinStockCountGroup(countId, groupId, {
        branch_id: branchId,
        ...payload,
      });
      await refreshCount();
      return group;
    },
    [branchId, countId, refreshCount]
  );

  const addCountGroupMember = useCallback(
    async (groupId, payload = {}) => {
      if (!branchId || !countId || !groupId) return null;
      const group = await inventoryService.addStockCountGroupMember(countId, groupId, {
        branch_id: branchId,
        ...payload,
      });
      await refreshCount();
      return group;
    },
    [branchId, countId, refreshCount]
  );

  const generalCounts = useMemo(
    () => counts.filter((row) => String(row?.count_type || "").toUpperCase() === "GENERAL"),
    [counts]
  );

  const activeGeneralCount = useMemo(
    () => generalCounts.find((row) => ACTIVE_STATUSES.includes(String(row?.status || "").toUpperCase())) || null,
    [generalCounts]
  );

  const blockers = useMemo(
    () => toArray(countSettings?.general_inventory_blockers),
    [countSettings?.general_inventory_blockers]
  );

  const visibleGeneralCounts = useMemo(() => {
    const normalizedSearch = String(countListSearch || "").trim().toLowerCase();
    return generalCounts.filter((count) => {
      const status = String(count?.status || "").toUpperCase();
      if (countListFilter !== "ALL" && status !== countListFilter) return false;
      if (!normalizedSearch) return true;
      const haystack = [
        count?.id,
        count?.comment,
        count?.created_by_name,
        count?.created_at,
        count?.status,
      ].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [countListFilter, countListSearch, generalCounts]);

  const branchName = currentBranch?.alias || currentBranch?.name || currentBranch?.description || "Sucursal actual";

  const openCount = useCallback(
    (count) => {
      if (!count?.id) return;
      const status = String(count?.status || "").toUpperCase();
      if (status === "DRAFT") {
        navigate(`/inventory/counts/general/capture?count_id=${count.id}`);
        return;
      }
      navigate(`/inventory/counts/${count.id}/review`);
    },
    [navigate]
  );

  const resolveBlocker = useCallback(
    (blocker) => {
      const target = blocker?.resolve_url || blocker?.view_url;
      if (!target) return;
      setSelectedBlocker(null);
      navigate(target);
    },
    [navigate]
  );

  const handleStartGeneralCount = useCallback(async () => {
    if (!branchId || blockers.length > 0 || !canStartGeneral) return;
    if (groupRolesDraft.length === 0) {
      showSnack("Debes definir al menos un grupo de conteo.", "warning");
      return;
    }
    setCreatingCount(true);
    try {
      const created = await stockCount.startCount({
        count_type: "GENERAL",
        group_count: groupRolesDraft.length,
        group_roles: serializeCountGroupRoles(groupRolesDraft),
      });
      setStartDialogOpen(false);
      setGroupCountDraft(1);
      setGroupRolesDraft([]);
      updateActiveGroupId(null);
      showSnack("Inventario general iniciado.", "success");
      await loadCounts();
      await loadCountSettings();
      if (created?.id) {
        navigate(`/inventory/counts/general/capture?count_id=${created.id}`, { replace: true });
      }
    } catch (error) {
      const pendingBlockers = toArray(error?.response?.data?.blockers);
      const blockerDetail = pendingBlockers.length
        ? ` ${pendingBlockers.map((row) => row?.detail || row?.label).filter(Boolean).slice(0, 3).join(" ")}`
        : "";
      showSnack(
        `${error?.response?.data?.detail || "No se pudo iniciar el inventario general."}${blockerDetail}`,
        "error"
      );
      await loadCounts();
      await loadCountSettings();
    } finally {
      setCreatingCount(false);
    }
  }, [
    blockers.length,
    branchId,
    canStartGeneral,
    groupRolesDraft,
    loadCountSettings,
    loadCounts,
    navigate,
    showSnack,
    stockCount,
    updateActiveGroupId,
  ]);

  const handleCancelCount = useCallback(async () => {
    if (!branchId || !cancelDialog.count?.id || !canCancelGeneral) return;
    setCancelSubmitting(true);
    try {
      await inventoryService.cancelStockCount(cancelDialog.count.id, { branch_id: branchId });
      setCancelDialog({ open: false, count: null });
      stockCount.clearCount();
      showSnack("Inventario anulado correctamente.", "success");
      await loadCounts();
      await loadCountSettings();
    } catch (error) {
      showSnack(error?.response?.data?.detail || "No se pudo anular el inventario.", "error");
    } finally {
      setCancelSubmitting(false);
    }
  }, [branchId, canCancelGeneral, cancelDialog.count?.id, loadCountSettings, loadCounts, showSnack, stockCount]);

  const hasActiveCount = Boolean(countId);
  const canAccessNewRoute = canStartGeneral || (hasCountIdParam && canAccessCapture);

  if ((hasActiveCount && !canAccessCapture) || (!hasActiveCount && !isNewRoute && !canAccessGeneral) || (!hasActiveCount && isNewRoute && !canAccessNewRoute)) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Sin permiso
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No tienes permisos para inventario general.
        </Typography>
      </Box>
    );
  }

  if (hasActiveCount) {
    const normalizedStatus = String(countStatus || "").toUpperCase();
    const editableInReview = canAdjustGeneral && ["PENDING_REVIEW", "REVIEWING"].includes(normalizedStatus);
    const captureReadOnly = (isReadOnly && !editableInReview) || (!canCountGeneral && !editableInReview);
    return (
      <>
        <StockCountPage
          title="Inventario general"
          subtitle="Conteo completo de la sucursal con operacion congelada"
          countId={countId}
          countStatus={countStatus}
          countAppliedAt={countAppliedAt}
          items={items}
          loading={stockCountLoading}
          error={stockCountError}
          filterMode={filterMode}
          setFilterMode={setFilterMode}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          filteredItems={filteredItems}
          addEntry={addEntry}
          updateEntry={updateEntry}
          voidEntry={voidEntry}
          resetItem={resetItem}
          submitCount={submitCount}
          isReadOnly={captureReadOnly}
          settingsSnapshot={settingsSnapshot}
          groups={groups}
          crosses={crosses}
          activeGroupId={activeGroupId}
          onActiveGroupChange={updateActiveGroupId}
          onCreateGroup={createCountGroup}
          onJoinGroup={joinCountGroup}
          onAddGroupMember={addCountGroupMember}
          isReviewCorrectionMode={editableInReview}
          reviewReturnPath={`/inventory/counts/${countId}/review`}
          headerAlert={
            normalizedStatus === "DRAFT" ? (
              <ActiveGeneralInventoryAlert activeInventory={{ id: countId, status: countStatus }} sx={{ mb: 2 }} />
            ) : null
          }
          onSubmitted={async (updatedCount) => {
            const targetId = updatedCount?.id || countId;
            stockCount.clearCount();
            if (activeGroupStorageKey) {
              window.sessionStorage.removeItem(activeGroupStorageKey);
            }
            navigate(`/inventory/counts/${targetId}/review`, { replace: true });
          }}
        />
        <CustomSnackBar open={snackOpen} setOpen={setSnackOpen} message={snackMsg} severity={snackSev} />
      </>
    );
  }

  if (isNewRoute) {
    return (
      <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.2}>
          <MainText
            title="Nuevo inventario general"
            subtitle="Checklist operativo antes de congelar la sucursal"
            icon={PlaylistAddCheckOutlined}
            width="50%"
          />
          <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh} disabled={settingsLoading || countsLoading}>
              Refrescar
            </Button>
            {canViewGeneral ? (
              <Button variant="outlined" onClick={() => navigate("/inventory/counts/general")}>
                Ver inventarios
              </Button>
            ) : null}
          </Stack>
        </Stack>

        <Alert severity="error">
          Al iniciar el inventario general de {branchName}, se bloquean ventas, notas y movimientos de productos de la sucursal hasta finalizarlo.
        </Alert>

        <ActiveGeneralInventoryAlert activeInventory={activeGeneralCount} />

        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Stack spacing={1.2}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              Condiciones para iniciar
            </Typography>
            {settingsLoading ? (
              <Typography variant="body2" color="text.secondary">
                Verificando bloqueos...
              </Typography>
            ) : blockers.length === 0 ? (
              <Alert severity="success">No hay bloqueos operativos detectados.</Alert>
            ) : (
              <Stack spacing={0.8}>
                <Alert severity="warning">
                  Resuelve estos bloqueos antes de iniciar el inventario general.
                </Alert>
                {blockers.map((blocker, index) => (
                  <Paper key={`${blocker?.type || blocker?.code || "blocker"}-${index}`} variant="outlined" sx={{ p: 1 }}>
                    <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={0.8}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                          {blocker?.label || "Bloqueo operativo"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {blocker?.summary || blocker?.detail || "Revisa la operacion de la sucursal antes de iniciar."}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.7} alignItems="center">
                        {blocker?.affected_count || blocker?.count ? (
                          <Chip size="small" label={blocker?.affected_count || blocker?.count} variant="outlined" />
                        ) : null}
                        <Button size="small" variant="outlined" onClick={() => setSelectedBlocker(blocker)}>
                          Ver
                        </Button>
                        {blocker?.can_resolve && blocker?.resolve_url ? (
                          <Button size="small" variant="contained" onClick={() => resolveBlocker(blocker)}>
                            Resolver
                          </Button>
                        ) : null}
                      </Stack>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                color="error"
                startIcon={<PlaylistAddCheckOutlined />}
                disabled={!canStartGeneral || creatingCount || blockers.length > 0 || settingsLoading}
                onClick={() => setStartDialogOpen(true)}
              >
                Iniciar inventario
              </Button>
            </Box>
          </Stack>
        </Paper>

        <Dialog
          open={startDialogOpen}
          onClose={() => {
            if (creatingCount) return;
            setStartDialogOpen(false);
          }}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Confirmar inventario general</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={1.2}>
              <Typography variant="body2" color="text.secondary">
                Se iniciara el conteo completo de {branchName}. La operacion de productos quedara congelada hasta aplicar o anular el inventario.
              </Typography>
              <CountGroupSetup
                groupCount={groupCountDraft}
                groups={groupRolesDraft}
                onGroupCountChange={setGroupCountDraft}
                onGroupsChange={setGroupRolesDraft}
                onGenerate={() => {
                  const count = Math.min(20, Math.max(1, Number(groupCountDraft) || 1));
                  setGroupCountDraft(count);
                  setGroupRolesDraft(
                    Array.from({ length: count }, (_, index) =>
                      groupRolesDraft[index] || { digitatorsText: "", countersText: "" }
                    )
                  );
                }}
              />
              <Alert severity="warning">
                Confirma solo cuando caja, ventas pendientes y notas manuales ya esten cerradas.
              </Alert>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStartDialogOpen(false)} disabled={creatingCount}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleStartGeneralCount}
              disabled={creatingCount || blockers.length > 0 || groupRolesDraft.length === 0}
            >
              {creatingCount ? "Iniciando..." : "Aceptar e iniciar"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={Boolean(selectedBlocker)}
          onClose={() => setSelectedBlocker(null)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>{selectedBlocker?.label || "Detalle del bloqueo"}</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={1.2}>
              <Typography variant="body2" color="text.secondary">
                {selectedBlocker?.summary || selectedBlocker?.detail || "Revisa este bloqueo antes de iniciar."}
              </Typography>
              <BlockerDetails blocker={selectedBlocker} />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSelectedBlocker(null)}>Cerrar</Button>
            {selectedBlocker?.can_resolve && selectedBlocker?.resolve_url ? (
              <Button variant="contained" onClick={() => resolveBlocker(selectedBlocker)}>
                Resolver
              </Button>
            ) : null}
          </DialogActions>
        </Dialog>

        <CustomSnackBar open={snackOpen} setOpen={setSnackOpen} message={snackMsg} severity={snackSev} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.2}>
        <MainText
          title="Inventario general"
          subtitle="Listado y control de inventarios completos de la sucursal"
          icon={Inventory2Outlined}
          width="50%"
        />
        <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh} disabled={settingsLoading || countsLoading}>
            Refrescar
          </Button>
          {canStartGeneral ? (
            <Button
              variant="contained"
              startIcon={<PlaylistAddCheckOutlined />}
              onClick={() => navigate("/inventory/counts/general/new")}
            >
              Nuevo inventario
            </Button>
          ) : null}
        </Stack>
      </Stack>

      {stockCountError ? <Alert severity="error">{stockCountError}</Alert> : null}

      <ActiveGeneralInventoryAlert activeInventory={activeGeneralCount} />

      <Paper variant="outlined" sx={{ p: 1.4 }}>
        <Stack spacing={1}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} spacing={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              Inventarios generales
            </Typography>
            <Chip size="small" label={`${generalCounts.length} registros`} variant="outlined" />
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }}>
            <Stack direction="row" spacing={0.6} useFlexGap flexWrap="wrap">
              {[
                { key: "ALL", label: "Todos" },
                { key: "DRAFT", label: "En captura" },
                { key: "PENDING_REVIEW", label: "Pendientes" },
                { key: "REVIEWING", label: "En revision" },
                { key: "APPLIED", label: "Aplicados" },
                { key: "CANCELED", label: "Anulados" },
              ].map((option) => (
                <Chip
                  key={option.key}
                  clickable
                  label={option.label}
                  color={countListFilter === option.key ? "primary" : "default"}
                  variant={countListFilter === option.key ? "filled" : "outlined"}
                  onClick={() => setCountListFilter(option.key)}
                />
              ))}
            </Stack>
            <TextField
              size="small"
              placeholder="Buscar por ID, usuario o fecha"
              value={countListSearch}
              onChange={(event) => setCountListSearch(event.target.value)}
              sx={{ minWidth: { xs: "100%", md: 300 }, ml: { md: "auto" } }}
            />
          </Stack>

          {countsLoading ? (
            <Typography variant="body2" color="text.secondary">
              Cargando inventarios...
            </Typography>
          ) : visibleGeneralCounts.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No hay inventarios generales para los filtros actuales.
            </Typography>
          ) : (
            <Stack spacing={0.9}>
              {visibleGeneralCounts.map((count, index) => {
                const statusMeta = getStatusMeta(count?.status);
                const status = String(count?.status || "").toUpperCase();
                return (
                  <Paper key={count.id} variant="outlined" sx={{ p: 1.1 }}>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={1} justifyContent="space-between">
                      <Box sx={{ minWidth: 0, display: "flex", gap: 1, alignItems: "flex-start" }}>
                        <Typography variant="body2" sx={{ fontWeight: 900, color: "text.secondary", minWidth: 28 }}>
                          #{index + 1}
                        </Typography>
                        <Box sx={{ minWidth: 0 }}>
                          <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              Inventario #{count.id}
                            </Typography>
                            <Chip size="small" color={statusMeta.color} label={statusMeta.label} />
                          </Stack>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.4 }}>
                            {formatDateTime(count?.created_at)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Contados: {count?.items_counted || 0} / {count?.items_total || 0}
                          </Typography>
                        </Box>
                      </Box>

                      <Stack direction="row" spacing={0.8} alignItems="center">
                        <Button size="small" variant="outlined" onClick={() => openCount(count)}>
                          {status === "DRAFT" ? "Retomar" : "Ver resumen"}
                        </Button>
                        {canCancelGeneral && ACTIVE_STATUSES.includes(status) ? (
                          <Button
                            size="small"
                            color="error"
                            disabled={cancelSubmitting}
                            onClick={() => setCancelDialog({ open: true, count })}
                          >
                            Anular
                          </Button>
                        ) : null}
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Stack>
      </Paper>

      <Dialog
        open={cancelDialog.open}
        onClose={() => {
          if (cancelSubmitting) return;
          setCancelDialog({ open: false, count: null });
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Anular inventario</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            El inventario quedara anulado y no modificara stock. Esta accion libera el bloqueo operativo de la sucursal.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog({ open: false, count: null })} disabled={cancelSubmitting}>
            Mantener
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={!canCancelGeneral || cancelSubmitting || !cancelDialog.count?.id}
            onClick={handleCancelCount}
          >
            {cancelSubmitting ? "Anulando..." : "Si, anular"}
          </Button>
        </DialogActions>
      </Dialog>

      <CustomSnackBar open={snackOpen} setOpen={setSnackOpen} message={snackMsg} severity={snackSev} />
    </Box>
  );
}

export default StockCountGeneralPage;
